import React, { ReactElement, useState, useEffect } from 'react';

import { FONT_SIZES } from '~/constants/typography';
import COLORS from '~/constants/colors';
import Gap from '~/components/flow/Gap';
import Break from '~/components/flow/Break';
import Title from '~/components/titles/Title';
import Prompt from '~/components/titles/Prompt';
import Subheader from '~/components/titles/Subheader';
import Header from '~/components/titles/Header';

import { Sounds } from '../../models';
import { readSounds } from '../../services';
import Help from '../../components/Help';
import examples from './examples';
import hipster from './assets/hipster.png';
import Exercise from './components/Exercise';

function Train(): ReactElement {
  const [sounds, setSounds] = useState<Sounds>();

  useEffect(() => {
    (async () => setSounds(await readSounds()))();
  }, []);

  // Styling
  const safeWidth = '70ch';
  const trainStyle = {
    minWidth: safeWidth,
    fontSize: FONT_SIZES.text,
  };
  const containerStyle = {
    width: `max(${safeWidth}, 100vw)`,
    textAlign: 'center' as const,
  };
  const topicStyle = {
    display: 'inline-block',
    width: 'min(65ch, 80%)',
    textAlign: 'center' as const,
  };
  const programDescStyle = {
    ...topicStyle,
    color: COLORS.silverChalice,
  };
  const wrapperStyle = {
    display: 'inline-block',
    width: 'min(75ch, 85%)',
    textAlign: 'left' as const,
  };
  const descStyle = {
    color: COLORS.doveGray,
  };
  const lightDescStyle = {
    color: COLORS.silverChalice,
  };
  const centerStyle = {
    textAlign: 'center' as const,
  };
  const youTubeStyle = {
    borderRadius: '0.5em',
  };

  return (
    <div style={trainStyle}>
      <div style={containerStyle}>
        <Help sounds={sounds} />
        <Break />
        <Break />
        <Break />
        <img width={100} alt="Hipster" src={hipster} />
        <Break />
        <Title>Kick Training 1.0</Title>
        <Break />
        <Subheader style={topicStyle}>Speed & BdB Combo</Subheader>
        <Break />
        <Prompt style={programDescStyle}>
          Amongst the most used sounds in beatbox but so few people know how to
          do them properly. Here’s a training program to help you add a proper
          kick to your kicks.
        </Prompt>
        <Break />
        <Break />
        <div style={wrapperStyle}>
          <Header>Context</Header>
          <Gap />
          <Prompt style={descStyle}>
            This training program focuses on improving “B” kick precision as
            well as extending that precision to the commonly fumbled “BdB”
            combo. It’s designed to make you give each sound the attention it
            deserves by pulling you between different tempos and structures.
            From this precision, you will develop comfort flexibly using your
            kick and “BdB” while making sure they have as much impact as your
            special sounds because, let’s be honest, you probably don’t know how
            to do the “d” in “BdB” well by itself. It’s not the sexiest
            technique out there, but you use it far more than any other sound
            you know so leveling it up will level you up.
          </Prompt>
        </div>
        <Break />
        <Break />
        <div style={wrapperStyle}>
          <Header>Training</Header>
          <Gap />
          <Prompt style={descStyle}>
            Repeat the following exercises for the B and d kicks
          </Prompt>
        </div>
        <Break />
        <Break />
        <div style={wrapperStyle}>
          <Header>Endurance</Header>
          <Gap />
          <Prompt style={descStyle}>
            Sound your kicks at 100% power till failure at 240bpm without
            missing a beat
            <ul>
              <li>Failure: stopping because of dry throat or muscle fatigue</li>
              <Gap />
              <li>
                Description: You will gradually feel your throat drying up as
                there is no space between each kick to swallow your saliva.
                Resist the temptation to swallow to build your endurance.
              </li>
              <Gap />
              <li>
                Note: if 240bpm is too easy for you, bump it up to a level that
                is moderately challenging.
              </li>
            </ul>
          </Prompt>
        </div>
        <Break />
        <div style={wrapperStyle}>
          <Header>Speed</Header>
          <Gap />
          <Prompt style={descStyle}>Do at 100bpm, 120bpm, 140bpm</Prompt>
          <Gap />
          <Exercise sounds={sounds} beat={examples[0]} />
        </div>
        <Break />
        <Break />
        <div style={wrapperStyle}>
          <Header>Strength</Header>
          <Gap />
          <Prompt style={lightDescStyle}>
            This should help you train your jaw, lip and breathing muscles to
            create the loudest kicks you possibly can make + train your ears to
            sense the contrast between each
          </Prompt>
          <Break />
          <Prompt style={descStyle}>
            Repeat 8 kicks in a row at 120bpm with 25%, 50%, 75%, 100% power.
            Repeat for 5 sets. With each set, increase your baseline “loudness“.
          </Prompt>
        </div>
        <Break />
        <Break />
        <div style={wrapperStyle}>
          <Header>Dexterity</Header>
          <Gap />
          <Prompt style={lightDescStyle}>
            This exercise incorporates use of rhythm + pitch + loudness dynamics
          </Prompt>
          <Break />
          <Prompt style={descStyle}>
            Lone ranger theme song performed with kicks from 2:16 to 2:30
            <ul>
              <li>Soft for first 2 bars, maximum loudness for the second</li>
              <Gap />
              <li>138bpm for 2 mins</li>
            </ul>
            <Gap />
            <div style={centerStyle}>
              <iframe
                title="BeatbloxTrainDexterityIFrame"
                width={475}
                height={268}
                src="https://www.youtube.com/embed/uUpDG680uew?fs=0&iv_load_policy=3&modestbranding=1&playsinline=1&enablejsapi=1&start=136"
                frameBorder="0"
                allow="fullscreen; encrypted-media"
                style={youTubeStyle}
              />
            </div>
          </Prompt>
        </div>
        <Break />
        <Break />
        <div style={wrapperStyle}>
          <Header>Combo</Header>
          <Gap />
          <Prompt style={lightDescStyle}>
            Do at 80bpm ~ trains your control over each individual sound and
            rhythm familiarity. You should try to get each sound loud and clear.
            5 sets.
          </Prompt>
          <Gap />
          <Exercise sounds={sounds} beat={examples[1]} />
        </div>
        <Break />
        <Break />
        <Prompt style={topicStyle}>
          Program Training Time: ~20 minutes
          <br />
          Do this daily and watch as your kick power, control and combo mastery
          reaches new heights with this training program<i>!</i>
        </Prompt>
        <Break />
        <Break />
      </div>
    </div>
  );
}

export default Train;
